import { el } from '../utils/dom.js';
import { Items, ItemUnits } from '../api/resources.js';

/*
 * Editor de lineas para prestamos / rentas / reservas.
 * options: { withPrice: bool, onlyAvailable: bool }
 *
 * lines collected via getLines() return [{ item_id?, item_unit_id?, quantity, price_per_day? }]
 */
export function LineEditor({ withPrice = false, onlyAvailable = true } = {}) {
  const lines = [];
  const list = el('div', { class: 'flex-col gap-2' });
  let itemsCache = null;
  let unitsCache = null;

  async function loadCatalog() {
    if (itemsCache) return { items: itemsCache, units: unitsCache };
    const [items, units] = await Promise.all([
      Items.list({ limit: 500 }).then((r) => r.data || []),
      ItemUnits.list({ limit: 1000, status: onlyAvailable ? 'disponible' : undefined })
        .then((r) => r.data || []),
    ]);
    itemsCache = items;
    unitsCache = units;
    return { items, units };
  }

  async function addLine() {
    const { items, units } = await loadCatalog();
    const id = Math.random().toString(36).slice(2, 8);
    const row = el('div', { class: 'card', style: 'background: var(--surface-2); padding: var(--sp-3);', dataset: { id } });

    const tabs = el('div', { class: 'flex gap-1 mb-2' },
      tabBtn('Item bulk', true,  () => switchKind('bulk')),
      tabBtn('Unidad serializada', false, () => switchKind('unit')),
    );

    const inner = el('div', { style: 'display:grid; grid-template-columns: 2fr 1fr 1fr auto; gap: var(--sp-2); align-items: end;' });

    const data = { kind: 'bulk', item_id: '', item_unit_id: '', quantity: 1, price_per_day: 0 };

    function tabBtn(label, active, onclick) {
      return el('button', {
        type: 'button',
        class: 'btn sm ' + (active ? 'primary' : ''),
        onclick: () => {
          [...tabs.children].forEach((t) => t.classList.remove('primary'));
          tabs.children[onclick._idx]?.classList.add('primary');
          onclick();
        },
      }, label);
    }
    tabs.children[0]._idx = 0;
    tabs.children[1]._idx = 1;

    function switchKind(kind) {
      data.kind = kind;
      renderInner();
    }

    function renderInner() {
      inner.replaceChildren();
      if (data.kind === 'bulk') {
        const bulkItems = items.filter((i) => i.kind === 'bulk');
        const sel = el('select', { class: 'select' },
          el('option', { value: '' }, '— Selecciona item bulk —'),
          ...bulkItems.map((i) => el('option', { value: i.id }, `${i.name}${i.brand ? ' · ' + i.brand : ''}`))
        );
        sel.value = data.item_id || '';
        sel.onchange = () => { data.item_id = sel.value; data.item_unit_id = ''; };
        inner.appendChild(field('Item bulk', sel));
        const qty = el('input', { class: 'input', type: 'number', min: 1, value: data.quantity, oninput: (e) => data.quantity = parseInt(e.target.value || '1', 10) });
        inner.appendChild(field('Cantidad', qty));
      } else {
        const sel = el('select', { class: 'select' },
          el('option', { value: '' }, '— Selecciona unidad —'),
          ...units.map((u) => el('option', { value: u.id }, `${u.item_name} — ${u.serial_number || u.internal_code || u.id}`))
        );
        sel.value = data.item_unit_id || '';
        sel.onchange = () => { data.item_unit_id = sel.value; data.item_id = ''; };
        inner.appendChild(field('Unidad', sel));
        const qty = el('input', { class: 'input', type: 'number', value: 1, disabled: true });
        inner.appendChild(field('Cantidad', qty));
      }

      if (withPrice) {
        const price = el('input', { class: 'input', type: 'number', step: '0.01', min: 0, value: data.price_per_day, oninput: (e) => data.price_per_day = Number(e.target.value || 0) });
        inner.appendChild(field('Precio/dia', price));
      } else {
        inner.appendChild(el('div', {}));
      }

      inner.appendChild(el('button', {
        type: 'button',
        class: 'btn danger sm',
        onclick: () => { row.remove(); const i = lines.findIndex((l) => l.id === id); if (i >= 0) lines.splice(i, 1); },
      }, 'Quitar'));
    }
    renderInner();

    row.append(tabs, inner);
    list.appendChild(row);
    lines.push({ id, get data() { return data; } });
  }

  function field(label, control) {
    return el('div', { class: 'field', style: 'margin: 0' },
      el('label', {}, label),
      control,
    );
  }

  const addBtn = el('button', { type: 'button', class: 'btn', onclick: () => addLine() }, '+ Agregar linea');

  function getLines() {
    return lines.map(({ data }) => {
      const out = { quantity: data.quantity || 1 };
      if (data.kind === 'bulk') out.item_id = data.item_id;
      else                       out.item_unit_id = data.item_unit_id;
      if (withPrice) out.price_per_day = Number(data.price_per_day || 0);
      return out;
    }).filter((l) => l.item_id || l.item_unit_id);
  }

  function validate() {
    const ls = getLines();
    if (ls.length === 0) return 'Agrega al menos una linea';
    for (const l of ls) {
      if (!l.item_id && !l.item_unit_id) return 'Selecciona un item o unidad en cada linea';
      if (withPrice && (l.price_per_day == null || l.price_per_day < 0)) return 'Precio invalido';
    }
    return null;
  }

  const root = el('div', { class: 'card mb-4' },
    el('div', { class: 'card-head' },
      el('h3', {}, 'Items'),
      addBtn,
    ),
    list,
  );

  return { root, getLines, validate };
}
